/**
 * <PlayerPicker> — pick a currently-online player to spawn a raid on top of.
 *
 * Replaces the free-text `at_player` input. The value is posted as
 * `at_player` and the backend asks the Palworld REST API for that player's
 * live coordinates, which skips the in-game M-map ↔ sav-coord conversion
 * entirely — so a typo here silently becomes "random spot" instead.
 *
 * Options are scoped to the SELECTED SERVER only: picking a player from
 * another server would spawn at coordinates on the wrong map. The free-text
 * escape hatch stays, because a player can be in-world without the presence
 * watcher having opened a tracked session yet, and manual summons are
 * deliberately unrestricted.
 *
 * Session shape from GET /admin/raids/presence (probed, not assumed):
 *   { user_id, name, seconds_online, raid_triggered, whispered }
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export function PlayerPicker({ server, value, onChange, slug = "manual" }) {
  const [sessions, setSessions] = useState([]);
  const [manual, setManual] = useState(false);

  // Refetch whenever the server selection changes. No polling — the presence
  // card on this page already polls, and a second timer would double the
  // RCON player-list load for no benefit.
  useEffect(() => {
    if (!server) { setSessions([]); return undefined; }
    let cancelled = false;
    api.get("/admin/raids/presence")
      .then(({ data }) => {
        if (cancelled) return;
        const row = (data?.servers || []).find((s) => s.server === server);
        setSessions(row?.sessions || []);
      })
      .catch(() => { if (!cancelled) setSessions([]); });
    return () => { cancelled = true; };
  }, [server]);

  const none = !sessions.length;

  if (manual) {
    return (
      <label className="text-[10px] text-[var(--muted)]">
        At player
        <input
          type="text"
          value={value}
          placeholder="exact in-game name"
          onChange={(e) => onChange(e.target.value)}
          className="input text-xs w-36 block mt-0.5"
          data-testid={`summon-now-at_player`}
        />
        <button type="button" onClick={() => setManual(false)}
          className="text-[9px] text-[var(--teal)] mt-0.5"
          data-testid="summon-now-at-player-use-list">
          use online list
        </button>
      </label>
    );
  }

  return (
    <label className="text-[10px] text-[var(--muted)]">
      At player
      <select
        value={value}
        disabled={none}
        onChange={(e) => onChange(e.target.value)}
        className="input text-xs w-36 block mt-0.5 disabled:opacity-50"
        data-testid={`summon-now-at-player-select-${slug}`}
      >
        {none ? (
          <option value="">No players online</option>
        ) : (
          <>
            <option value="">Random spot</option>
            {sessions.map((s) => (
              <option key={s.user_id || s.name} value={s.name}>
                {s.name}
                {Number.isFinite(s.seconds_online)
                  ? ` · ${Math.floor(s.seconds_online / 60)}m`
                  : ""}
              </option>
            ))}
          </>
        )}
      </select>
      <button type="button" onClick={() => setManual(true)}
        className="text-[9px] text-[var(--teal)] mt-0.5"
        data-testid="summon-now-at-player-type-name">
        type a name
      </button>
    </label>
  );
}
